import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { myFilesFolderView, getEnvelopeSenderList } from '../../../../services/CommonService';
import { toast } from 'react-toastify';
import image from '../../../../assets/svg/image.svg';
import docx from '../../../../assets/svg/docx.svg';
import folder from '../../../../assets/svg/folder.svg';
import pdf from '../../../../assets/svg/pdf.svg';
// import './SiteFilesModal.css'; // Ensure this CSS file is included

const SiteFilesModal = ({ show, handleClose, onSelectFiles }) => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    getEnvelopeSenderList()
      .then(response => {
        const senderData = response.data.data;
        if (senderData.length > 0 && senderData[0].id) {
          setUserId(Number(senderData[0].id));
        }
      })
      .catch(err => {
        toast.error('Failed to fetch sender list!');
      });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!userId || loading) return;

      setLoading(true);
      try {
        const payload = {
          user_id: userId,
          folder_id: currentFolder?.id || null,
        };

        const response = await myFilesFolderView(payload);

        if (response.data.message !== 'success') {
          throw new Error(response.data.message || 'Failed to fetch data');
        }

        if (isMounted) {
          const responseData = response.data.data || {};
          const receivedFolders = responseData.folders || [];
          const receivedFiles = responseData.files || responseData.data?.files || [];

          setFolders(receivedFolders);
          setFiles(receivedFiles);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(err.message || 'Failed to fetch data');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (show) fetchData();

    return () => {
      isMounted = false;
    };
  }, [userId, currentFolder?.id, show]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const highlightText = useCallback((text, search) => {
    if (!search.trim()) return text;

    try {
      const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return text.split(regex).map((part, index) =>
        regex.test(part) ? (
          <span key={index} className="highlight">
            {part}
          </span>
        ) : (
          part
        )
      );
    } catch (err) {
      console.error('Error in highlightText:', err);
      return text;
    }
  }, []);

  const handleFolderClick = (folder) => {
    setFolderStack((prev) => (currentFolder ? [...prev, currentFolder] : prev));
    setCurrentFolder(folder);
    setSearchTerm('');
  };

  const handleBack = () => {
    if (folderStack.length > 0) {
      const newStack = [...folderStack];
      const previousFolder = newStack.pop();
      setFolderStack(newStack);
      setCurrentFolder(previousFolder);
      setSearchTerm('');
    } else {
      setCurrentFolder(null);
      setFolderStack([]);
      setSearchTerm('');
    }
  };

  const getBreadcrumbs = () => {
    const crumbs = [];

    crumbs.push(
      <span
        key="root"
        onClick={() => {
          setCurrentFolder(null);
          setFolderStack([]);
          setSearchTerm('');
        }}
        className={`breadcrumbItem ${!currentFolder ? 'activeCrumb' : ''}`}
        style={{ cursor: 'pointer' }}
      >
        Home
      </span>
    );

    folderStack.forEach((folder, index) => {
      crumbs.push(
        <span key={`separator-${folder.id}`} className="breadcrumbSeparator">
          <i className="bi bi-chevron-right mx-2 textMuted" />
        </span>
      );
      crumbs.push(
        <span
          key={folder.id}
          onClick={() => {
            setCurrentFolder(folder);
            setFolderStack(folderStack.slice(0, index + 1));
            setSearchTerm('');
          }}
          className={`breadcrumbItem ${currentFolder?.id === folder.id ? 'activeCrumb' : ''}`}
          style={{ cursor: 'pointer' }}
          title={folder.folder_name} // Add tooltip for breadcrumb folder names
        >
          {highlightText(folder.folder_name, searchTerm)}
        </span>
      );
    });

    if (currentFolder && (folderStack.length === 0 || folderStack[folderStack.length - 1]?.id !== currentFolder.id)) {
      crumbs.push(
        <span key={`separator-current`} className="breadcrumbSeparator">
          <i className="bi bi-chevron-right mx-2 textMuted" />
        </span>
      );
      crumbs.push(
        <span key={currentFolder.id} className="breadcrumbItem activeCrumb" title={currentFolder.folder_name}>
          {highlightText(currentFolder.folder_name, searchTerm)}
        </span>
      );
    }

    return <div className="d-flex align-items-center flex-wrap">{crumbs}</div>;
  };

  const getFileIconAndClass = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return {
          icon: (
            <div className="icon-background">
              <img src={pdf} alt="PDF icon" style={{ height: 40, width: 40 }} />
            </div>
          ),
          class: 'file-card-pdf pdf-border',
          type: 'PDF',
        };
      case 'jpg':
      case 'jpeg':
      case 'png':
        return {
          icon: (
            <div className="icon-background">
              <img src={image} alt="Image icon" style={{ height: 40, width: 40 }} />
            </div>
          ),
          class: 'file-card-image image-border',
          type: 'IMAGE',
        };
      case 'gif':
        return {
          icon: (
            <div className="icon-background">
              <svg className="file-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V8L14 2Z"
                  fill="#4dff88"
                />
                <path d="M14 2V8H19" fill="#66ff99" />
                <path d="M9 12L11 14L14 11L16 14H8L9 12Z" fill="#fff" />
              </svg>
            </div>
          ),
          class: 'file-card-image image-border',
          type: 'IMAGE',
        };
      case 'doc':
      case 'docx':
        return {
          icon: (
            <div className="icon-background">
              <img src={docx} alt="DOC icon" style={{ height: 40, width: 40 }} />
            </div>
          ),
          class: 'file-card-doc doc-border',
          type: 'DOC',
        };
      default:
        return {
          icon: (
            <div className="icon-background">
              <img src={folder} alt="Default icon" style={{ height: 40, width: 40 }} />
            </div>
          ),
          class: 'file-card-doc default-border',
          type: 'FILE',
        };
    }
  };

  const handleFileSelect = (file) => {
    setSelectedFiles((prev) =>
      prev.some((f) => f.id === file.id) ? prev.filter((f) => f.id !== file.id) : [...prev, file]
    );
  };

  const handleSelectFiles = () => {
    onSelectFiles(
      selectedFiles.map((file) => ({
        file_name: file.file_name,
        file_path: file.file_path,
        size: file.size,
      }))
    );
    handleClose();
  };

  const filteredItems = useCallback(() => {
    let filteredFolders = folders;
    let filteredFiles = files;

    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filteredFolders = folders.filter((folder) =>
        folder.folder_name.toLowerCase().includes(lowerSearchTerm)
      );
      filteredFiles = files.filter((file) =>
        file.file_name.toLowerCase().includes(lowerSearchTerm)
      );
    }

    return { filteredFolders, filteredFiles };
  }, [folders, files, searchTerm]);

  const { filteredFolders, filteredFiles } = filteredItems();

  const renderFolderItem = (folder, index) => (
    <div
      key={`folder-${folder.id}`}
      className={`file-card file-card-folder folder-border ${currentFolder?.id === folder.id ? 'active-folder' : ''
        }`}
      onClick={() => handleFolderClick(folder)}
      style={{ cursor: 'pointer' }}
    >
      <div className="file-icon-container">
        <div className="icon-background">
          <svg
            className="plain file-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 6H9L11 8H20C20.5523 8 21 8.44772 21 9V19C21 19.5523 20.5523 20 20 20H4C3.44772 20 3 19.5523 3 19V7C3 6.44772 3.44772 6 4 6Z"
              fill="#ffcc00"
            />
            <path d="M9 6L11 8H20V9H9L7 7H4V6H9Z" fill="#ffd633" />
          </svg>
        </div>
      </div>
      <div className="file-name" title={folder.folder_name}>
        {highlightText(folder.folder_name, searchTerm)}
      </div>
      <div className="file-count position-absolute top-right bg-dark-subtle rounded-circle d-flex justify-content-center align-items-center px-2 py-0">
        {folder.file_count}
      </div>
      <div className="file-type">FOLDER</div>
    </div>
  );

  const renderFileItem = (file, index) => {
    const { icon, class: cardClass, type } = getFileIconAndClass(file.file_name);

    return (
      <div
        key={`file-${file.id}`}
        className={`file-card ${cardClass} ${selectedFiles.some((f) => f.id === file.id) ? 'selected' : ''}`}
        style={{ cursor: 'pointer' }}
        onClick={() => handleFileSelect(file)}
      >
        <div className="file-icon-container">{icon}</div>
        <div className="file-info">
          <div className="file-name" title={file.file_name}>
            {highlightText(file.file_name, searchTerm)}
          </div>
          <div className="file-type">{type}</div>
        </div>
        {/* <input
          type="checkbox"
          className="file-checkbox"
          checked={selectedFiles.some((f) => f.id === file.id)}
          onChange={() => handleFileSelect(file)}
        /> */}
        {/* <div className="file-ellipsis">...</div> */}
      </div>
    );
  };


  const renderEmptyState = () => (
    <div className="text-center text-muted p-4">No folders or files found.</div>
  );

  return (
    <div className="modalOverlay">
      <div className="siteFilesModal">
        <div className="modalHeader">
          <h5>Select Files</h5>
          <button onClick={handleClose} className="btnClose" aria-label="Close modal">
            <i className="bi bi-x-circle" aria-hidden="true" />
          </button>
        </div>
        <div className="modalBody">
          <div className="siteFilesGrid">
            {loading && (
              <div className="loaderOverlay">
                <img src="/images/loader.gif" alt="loader" />
              </div>
            )}
            <div className="containerMain rounded">
              <div className="folderHeader rounded">
                <div className="headerContainer">
                  <div className="breadcrumbNav">{getBreadcrumbs()}</div>
                </div>
                <div className="myFilesFolderSearchContainer mb-3">
                  <input
                    type="text"
                    className="form-control myFilesFolderSearchInput"
                    placeholder="Type to search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                  />
                </div>
              </div>
              <div className="fileListItem">
                {filteredFolders.length > 0 || filteredFiles.length > 0 ? (
                  <>
                    {filteredFolders.map((folder, index) => renderFolderItem(folder, index))}
                    {filteredFiles.map((file, index) => renderFileItem(file, index))}
                  </>
                ) : (
                  renderEmptyState()
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="modalFooter">
          <button onClick={handleClose} className="btn btnOutlineSecondary">
            Cancel
          </button>
          <button
            onClick={handleSelectFiles}
            className="btn btn-save"
            disabled={selectedFiles.length === 0}
          >
            <i className="bi bi-check-circle me-2" aria-hidden="true" />
            Select {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''} Files
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteFilesModal;